import { ReactUtils, typeUtils } from '@festina-life/core'
import { IconButton } from '@festina-life/ui/actions'
import { Alert, Tooltip } from '@festina-life/ui/feedback'
import { HeadlessUiComboboxOption, selectStyles } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { Icon, Stack } from '@festina-life/ui/structure'
import cx from 'classnames'

import { RegisteredAction } from '../../../hooks/navigation/RegisteredAction'
import { ErrorMessageBody } from '../../errorFallback/ErrorMessageBody'
import { OptionWarnings } from './OptionWarnings'
import type { SearchableItemGroup, SearchableOption } from './types'
import { useCommandPaletteLocalStorage } from './useCommandPaletteLocalStorage'
import { checkApiDataResponse } from './utils'

const validateKey =
  typeUtils.createValidator<
    Pick<SearchableOption, 'hasRequiredComponents' | 'apiResponse'>
  >()

const isSearchableOption = (value: unknown): value is SearchableOption =>
  typeUtils.hasField(value, validateKey('hasRequiredComponents')) &&
  typeUtils.hasField(value, validateKey('apiResponse'))

type Props = {
  comboboxId?: string
} & (
  | {
      value: SearchableOption
      hasNestedChildren: true
      isNested?: never
      group: SearchableItemGroup
    }
  | {
      isNested: true
      hasNestedChildren?: never
      value: NonNullable<
        NonNullable<SearchableOption['nestedView']>['nestedChildren']
      >[number]
      group: SearchableItemGroup
    }
  | {
      value: SearchableOption
      group?: never
    }
  | {
      value: SearchableOption
      group?: SearchableItemGroup
    }
)

export const Option = ({ value, comboboxId, group }: Props) => {
  const translate = useTranslate()

  const {
    isSelected,
    title,
    presentation,
    hasSufficientAccessRights,
    isNested,
    isWorkflow,
  } = value

  const hasNestedChildren = typeUtils.hasField(value, 'hasNestedChildren')
    ? value.hasNestedChildren
    : false

  const optionValue: SearchableOption = isSearchableOption(value)
    ? value
    : {
        ...value,
        apiResponse: undefined,
      }

  const { apiResponse, nestedView, favouriteKey } = optionValue

  //If it is not provided, we assume the option should be enabled
  const isDisabledByAccessRights = hasSufficientAccessRights === false

  const [favourites, setFavourites] = useCommandPaletteLocalStorage()

  const favouriteKeyToStore = favouriteKey ?? ''

  const isAlreadyFavourited =
    !!group && !!favourites[group].favouriteKeys.includes(favouriteKeyToStore)

  const handleRemoveOrAddFavourite = () => {
    // In the command palette we use fallbackOptions that do not have a group
    if (!group) {
      return
    }

    const copyOfFavourites = structuredClone(favourites)

    const index =
      copyOfFavourites[group].favouriteKeys.indexOf(favouriteKeyToStore)

    if (index === -1) {
      copyOfFavourites[group].favouriteKeys.push(favouriteKeyToStore)
    } else {
      copyOfFavourites[group].favouriteKeys.splice(index, 1)
    }

    setFavourites(copyOfFavourites)
  }

  const hasData = checkApiDataResponse(apiResponse)
  const error = apiResponse?.error

  return nestedView && group ? (
    <RegisteredAction group={group} parentView={optionValue} {...nestedView} />
  ) : (
    <ReactUtils.ConditionalWrap
      condition={isDisabledByAccessRights || !hasData || !!error}
      wrap={(children) => (
        <Tooltip
          shouldRenderInPortal={false} // Can't make it show otherwise
          tip={
            <Stack className="flex-col">
              {isDisabledByAccessRights
                ? translate('option.insufficientAccessRights')
                : translate('option.noDataAvailable')}
              {error && <ErrorMessageBody error={error} />}
            </Stack>
          }
        >
          <div>{children}</div>
        </Tooltip>
      )}
    >
      <HeadlessUiComboboxOption
        className={({ focus }) =>
          cx(
            selectStyles.option({
              hasFocus: focus,
              isSelected: false, // Temp bypass since current selected styles are too pronounced, selectStyles.option should be fixed to have less pronounced selected styles and consistent check marks too.
              isDisabled: isDisabledByAccessRights || !hasData,
            }),
            'mx-1 group',
          )
        }
        value={optionValue}
        data-testid={title}
        disabled={isDisabledByAccessRights || !hasData}
        id={comboboxId}
      >
        <div
          className="grow flex" // ensures content takes up full space for proper alignment of right side icons/labels
        >
          {apiResponse?.error || apiResponse?.isLoading ? (
            <Alert
              variant="mini"
              severity={apiResponse?.error ? 'error' : undefined}
              isLoading={apiResponse?.isLoading}
              className="mr-1"
            />
          ) : null}
          {isWorkflow && 'ꗬ '}
          {presentation ?? title}
        </div>
        <div className="flex gap-1">
          {!isNested && group && !!favouriteKey && (
            <div
              // extra div was needed for consistent behavior for the negative margin around the button
              className="-my-1 -mx-1.5" // negative margin to compensate for padding inside IconButton
            >
              <IconButton
                iconName="Star"
                // Is on shown on hover with CSS styling, as using HeaddlessUiComboboxOption's focus does not work when disabled.
                className={cx('opacity-0 transition-opacity', {
                  'opacity-100': isAlreadyFavourited,
                  'group-hover:opacity-100': !isAlreadyFavourited,
                })}
                isSelected={isAlreadyFavourited}
                extraProps={{
                  onMouseDown: (e) => {
                    // needs to be mouse down to trigger before option select
                    e.stopPropagation()
                    e.preventDefault()
                    handleRemoveOrAddFavourite()
                  },
                }}
              />
            </div>
          )}
          {group && (
            <OptionWarnings
              group={group}
              hasNestedChildren={!!hasNestedChildren}
              warnings={value.warnings}
              isAvailable={hasData}
            />
          )}
          {isSelected && <Icon name="Check" />}
        </div>
      </HeadlessUiComboboxOption>
    </ReactUtils.ConditionalWrap>
  )
}
